/**
 * Геометрия клавиатуры и близость клавиш.
 *
 * Модель простая, но достаточная: ряды смещены друг относительно друга на
 * фиксированный шаг (stagger), как на реальной клавиатуре. Расстояние между
 * клавишами используется для классификации ошибок «промах по соседней».
 */

import type { KeyDefinition, KeyRow } from './types.js';

/** Вертикальная позиция ряда. */
const ROW_INDEX: Readonly<Record<KeyRow, number>> = {
  number: 0,
  top: 1,
  home: 2,
  bottom: 3,
  space: 4,
};

/** Горизонтальное смещение ряда относительно ряда цифр. */
const ROW_STAGGER: Readonly<Record<KeyRow, number>> = {
  number: 0,
  top: 0.5,
  home: 0.75,
  bottom: 1.25,
  space: 3.5,
};

/** Максимальное расстояние, при котором клавиши считаются соседними. */
export const ADJACENCY_THRESHOLD = 1.35;

/** Координаты клавиши в условных единицах ширины клавиши. */
export function positionOf(key: KeyDefinition): readonly [number, number] {
  return [key.column + ROW_STAGGER[key.row], ROW_INDEX[key.row]];
}

/** Евклидово расстояние между клавишами. */
export function distanceBetween(a: KeyDefinition, b: KeyDefinition): number {
  const [ax, ay] = positionOf(a);
  const [bx, by] = positionOf(b);
  return Math.hypot(ax - bx, ay - by);
}

/** Являются ли клавиши физически соседними. */
export function areAdjacent(a: KeyDefinition, b: KeyDefinition): boolean {
  if (a === b) return false;
  return distanceBetween(a, b) <= ADJACENCY_THRESHOLD;
}
